import { StringIndexedDict } from "../api/GenericTypes";
import {
  ZontalInformationPackageProfile,
  ZontalInformationPackageProfileFilters,
  ZontalInformationPackageProfileSuggestions,
  ZontalInformationPackageSortingOptionsConsts,
  zontalIPPConstants,
} from "../api/Zontal";
import {
  GenericEntityVirtualizedEntitySelectFormFieldProps,
  GenericVirtualizedEntitySelectFormFieldControllerWrapper,
} from "../common/formfields/GenericVirtualizedSelectFormField/GenericVirtualizedEntitySelectFormFieldControllerWrapper";
import { ResourceName } from "../main/Routing";
import { Button } from "../common/buttons/Button/Button";
import { API } from "../api/Api";
import { LucideIcon } from "../common/icon/LucideIcon";
import { useCallback, useContext, useState } from "react";
import { useInfiniteListZontalIPP } from "./useZontalAPI";
import { SessionContext } from "../common/contexts/SessionContext";
import { renderError } from "../common/helperfunctions/ApiError";

export const ZontalIcon = ({ style, loading }: { style?: React.CSSProperties; loading?: boolean }) => {
  if (loading)
    return (
      <img
        src={`${process.env.PUBLIC_URL}/assets/logos/zontal_loader.svg`}
        className="feather"
        alt="ZONTAL"
        style={style}
      />
    );
  return (
    <img
      src={`${process.env.PUBLIC_URL}/assets/logos/favicon_zontal.svg`}
      className="feather"
      alt="ZONTAL"
      style={style}
    />
  );
};

export const ZontalIPPSelectForm = <Entity extends StringIndexedDict>(
  props: GenericEntityVirtualizedEntitySelectFormFieldProps<
    Entity,
    ZontalInformationPackageProfileSuggestions,
    ZontalInformationPackageProfileFilters
  >
) => {
  const useHook = (filters: ZontalInformationPackageProfileFilters) => {
    return useInfiniteListZontalIPP(
      `${zontalIPPConstants.resource}/informationPackageProfile/suggestions` as ResourceName,
      { ...filters },
      { enabled: true }
    );
  };

  return (
    <GenericVirtualizedEntitySelectFormFieldControllerWrapper<
      Entity,
      ZontalInformationPackageProfileSuggestions,
      ZontalInformationPackageProfileFilters
    >
      {...props}
      filters={{ sortBy: [{ property: "NAME", sortDirection: "ASC" }], ...props.filters }}
      entityConstants={{
        ...zontalIPPConstants,
        entityPlural: "ZONTAL information package profiles",
        entitySingular: "ZONTAL information package profile",
        resource: "zontal/information_package/informationPackageProfile" as ResourceName,
      }}
      orderByOptions={ZontalInformationPackageSortingOptionsConsts}
      allowCreateEntity={false}
      queryResultOverride={useHook}
    />
  );
};

export const ZontalEnteredBySelectForm = <Entity extends StringIndexedDict>(
  props: GenericEntityVirtualizedEntitySelectFormFieldProps<
    Entity,
    ZontalInformationPackageProfileSuggestions,
    ZontalInformationPackageProfileFilters
  >
) => {
  const useHook = (filters: ZontalInformationPackageProfileFilters) => {
    return useInfiniteListZontalIPP(
      `${zontalIPPConstants.resource}/enteredBy/suggestions` as ResourceName,
      { ...filters },
      { enabled: true }
    );
  };

  return (
    <GenericVirtualizedEntitySelectFormFieldControllerWrapper<
      Entity,
      ZontalInformationPackageProfileSuggestions,
      ZontalInformationPackageProfileFilters
    >
      {...props}
      filters={{ sortBy: [{ property: "NAME", sortDirection: "ASC" }], ...props.filters }}
      entityConstants={{
        ...zontalIPPConstants,
        entityPlural: "ZONTAL users",
        entitySingular: "ZONTAL user",
        resource: "zontal/information_package/enteredBy" as ResourceName,
      }}
      orderByOptions={ZontalInformationPackageSortingOptionsConsts}
      allowCreateEntity={false}
      queryResultOverride={useHook}
    />
  );
};

export const ZontalModifiedBySelectForm = <Entity extends StringIndexedDict>(
  props: GenericEntityVirtualizedEntitySelectFormFieldProps<
    Entity,
    ZontalInformationPackageProfileSuggestions,
    ZontalInformationPackageProfileFilters
  >
) => {
  const useHook = (filters: ZontalInformationPackageProfileFilters) => {
    return useInfiniteListZontalIPP(
      `${zontalIPPConstants.resource}/modifiedBy/suggestions` as ResourceName,
      { ...filters },
      { enabled: true }
    );
  };

  return (
    <GenericVirtualizedEntitySelectFormFieldControllerWrapper<
      Entity,
      ZontalInformationPackageProfileSuggestions,
      ZontalInformationPackageProfileFilters
    >
      {...props}
      filters={{ sortBy: [{ property: "NAME", sortDirection: "ASC" }], ...props.filters }}
      entityConstants={{
        ...zontalIPPConstants,
        entityPlural: "ZONTAL users",
        entitySingular: "ZONTAL user",
        resource: "zontal/information_package/modifiedBy" as ResourceName,
      }}
      orderByOptions={ZontalInformationPackageSortingOptionsConsts}
      allowCreateEntity={false}
      queryResultOverride={useHook}
    />
  );
};

export const getZONTALDatasetZip = (api: API, id: string) => {
  const { hostname, port, protocol } = api.getUrlComponents();
  const url = `${protocol}//${hostname}:${port}/${api.getGroup()}/api/0.1/${zontalIPPConstants.resource}/${id}/zip?${
    api.getAuthentication().type
  }=${encodeURIComponent(api.getAuthentication().value)}`;
  api.triggerDownloadLink(url, `ZONTAL_IP_${id}.zip`);
};

interface DownloadZONTALDatasetProps {
  btnStyle?: string;
  api: API;
  id: string;
  label?: string;
}
export const DownloadZONTALDataset = ({
  btnStyle = "btn btn-primary",
  api,
  id,
  label = "Download",
}: DownloadZONTALDatasetProps) => {
  const [isLoading, setIsLoading] = useState(false);
  return (
    <Button
      className={btnStyle}
      onClick={() => {
        if (!isLoading) {
          setIsLoading(() => true);
          getZONTALDatasetZip(api, id);
          setIsLoading(() => false);
        }
      }}
      disabled={isLoading}
      loading={isLoading}
      title="Download"
    >
      <LucideIcon name="download" /> {label}
    </Button>
  );
};

export const useDownloadZontalDatasets = (onFinishCallback?: () => void) => {
  const { api } = useContext(SessionContext);
  const [isLoading, setIsLoading] = useState(false);

  const download = useCallback(
    async (ids: ZontalInformationPackageProfile["id"][]) => {
      setIsLoading(true);
      try {
        const response: ArrayBuffer = await api.post(`${zontalIPPConstants.resource}/zip`, { ids: ids });
        const blob = new Blob([response], { type: "application/zip" });
        const url = window.URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        const isoDate = new Date().toISOString().replace(/[:.]/g, "-");
        link.setAttribute("download", `ZONTAL_InformationPackage_bundle_${isoDate}.zip`);

        document.body.appendChild(link);
        link.click();
        link.parentNode?.removeChild(link);
      } catch (error) {
        renderError(error);
      } finally {
        setIsLoading(false);
        onFinishCallback?.();
      }
    },
    [api, onFinishCallback]
  );

  return { download, isLoading };
};
